// Small page-local pieces shared by the voice pages (agents, numbers, calls):
// the « À vérifier » card, the sticky save bar, radio choice tiles and the
// transcript bubbles. Layout lives in VoiceSetup.css under .voice-v2__*.
// Proposals for src/design/ui.js (the channels pages carry similar ones).
import React from 'react';
import { Check, ChevronRight, TriangleAlert } from 'lucide-react';
import { Badge, Card } from '../../design/ui';
import './VoiceSetup.css';

const cx = (...names) => names.filter(Boolean).join(' ');

// Plural keys (key_one / key_other in the locale files); the French default
// follows the count.
export const pluralT = (t) => (key, one, other, options) => t(key, options.count === 1 ? one : other, options);

export const formatCount = (value, lang) => new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR').format(value || 0);

// A duration as a tile value: « 2 min » big, « 24 s » as the unit, so it fits
// a narrow tile (« 2 min 24 s » at the value size is cut at 1 024 px).
export const durationTile = (seconds) => {
    const total = Math.round(seconds || 0);
    return { value: `${Math.floor(total / 60)} min`, unit: `${String(total % 60).padStart(2, '0')} s` };
};

// items: [{ key, tone: 'danger' | 'warning', title, detail, action: { label, onClick } }]
export const AttentionCard = ({ items, t }) => {
    if (!items.length) return null;
    return (
        <Card
            icon={TriangleAlert}
            iconTone="warning"
            title={t('voiceUi.attention.title', 'À vérifier')}
            badge={<Badge tone="warning">{items.length}</Badge>}
            flush
            className="voice-v2__attention"
        >
            <ul className="u-list">
                {items.map((item) => (
                    <li className="u-row" key={item.key}>
                        <Badge tone={item.tone} dot>
                            {item.tone === 'danger' ? t('voiceUi.attention.blocking', 'Bloquant') : t('voiceUi.attention.watch', 'À surveiller')}
                        </Badge>
                        <div className="u-row__main">
                            <div className="u-row__title">{item.title}</div>
                            {item.detail && <div className="u-row__meta u-row__meta--wrap">{item.detail}</div>}
                        </div>
                        {item.action && (
                            <div className="u-row__side">
                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={item.action.onClick}>
                                    {item.action.label}
                                    <ChevronRight aria-hidden="true" />
                                </button>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </Card>
    );
};

export const SaveBar = ({ dirty, saving, onSave, onReset, labels }) => (
    <div className={cx('u-card voice-v2__savebar', dirty && 'is-dirty')} role="region" aria-label={labels.region}>
        <span className="voice-v2__savebar-state">
            {dirty
                ? <Badge tone="warning" dot>{labels.dirty}</Badge>
                : <span className="voice-v2__saved"><Check strokeWidth={2} aria-hidden="true" />{labels.clean}</span>}
        </span>
        {dirty && (
            <button type="button" className="u-btn u-btn--secondary" onClick={onReset} disabled={saving}>
                {labels.reset}
            </button>
        )}
        <button type="button" className="u-btn u-btn--primary" onClick={onSave} disabled={saving || !dirty}>
            {saving ? labels.saving : labels.save}
        </button>
    </div>
);

// options: [{ value, title, hint }]
export const ChoiceTiles = ({ label, options, value, onChange, compact }) => (
    <div className={cx('voice-v2__choices', compact && 'voice-v2__choices--compact')} role="radiogroup" aria-label={label}>
        {options.map((option) => {
            const selected = option.value === value;
            return (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={cx('voice-v2__choice', selected && 'is-selected')}
                    onClick={() => onChange(option.value)}
                >
                    <span className="voice-v2__choice-mark" aria-hidden="true">{selected && <Check strokeWidth={2.4} />}</span>
                    <span className="voice-v2__choice-text">
                        <strong>{option.title}</strong>
                        {option.hint && <small>{option.hint}</small>}
                    </span>
                </button>
            );
        })}
    </div>
);

// One spoken turn: who speaks, the words, when. role: caller | agent | tool.
export const TranscriptTurn = ({ role, who, text, time, icon: Icon }) => (
    <li className={cx('voice-v2__turn', `voice-v2__turn--${role}`)}>
        <span className="voice-v2__turn-who" aria-hidden={Icon ? 'true' : undefined}>
            {Icon ? <Icon strokeWidth={1.8} /> : null}
        </span>
        <div className="voice-v2__bubble">
            <span className="voice-v2__bubble-head">
                <span className="voice-v2__bubble-name">{who}</span>
                {time && <time>{time}</time>}
            </span>
            <p>{text}</p>
        </div>
    </li>
);

export const scrollToId = (id) => {
    const node = document.getElementById(id);
    if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
